import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { without } from 'lodash';
import { v4 } from 'uuid';
import { useDispatch, useSelector } from 'react-redux';
import * as Yup from 'yup';
import OnboardingPackageSelectionForm from '../Forms/OnboardingPackageSelectionForm/OnboardingPackageSelectionForm';
import {
  getAvailablePackages,
  getOrganizations,
} from '../../Services/OnboardingService';
import { actions as OnboardingActions } from '../../Ducks/Onboarding.duck';
import { useIntl } from 'react-intl';

const OnboardingPackageSelectionStep = ({ formRef, onSubmit, initialValues }) => {
  const [isLoadingOrganizations, setIsLoadingOrganizations] = useState(false);
  const [isLoadingPackages, setIsLoadingPackages] = useState(true);
  const [isLoadingValidations, setIsLoadingValidations] = useState(true);

  const [connectionName, setConnectionName] = useState(
    initialValues.connectionName ? initialValues.connectionName : '',
  );
  const [organizations, setOrganizations] = useState(null);
  const [connectionNameChunks, setConnectionNameChunks] = useState([]);
  const [validationSchema, setValidationSchema] = useState(Yup.object().shape({}));
  const [availablePackages, setAvailablePackages] = useState(null);
  const [selectedPackages, setSelectedPackages] = useState(initialValues.packages ? initialValues.packages : []);
  const dispatch = useDispatch();

  const { user } = useSelector(state => state.AppReducer);

  const intl = useIntl();
  const loading = isLoadingOrganizations || isLoadingPackages || isLoadingValidations;

  const loadAvailablePackages = async packages => {
    setIsLoadingPackages(true);
    const response = await getAvailablePackages(packages);
    setAvailablePackages(response.data['hydra:member']);
    setIsLoadingPackages(false);
  };

  const loadOrganizations = async () => {
    setIsLoadingOrganizations(true);
    const response = await getOrganizations();
    setOrganizations(response.data['hydra:member']);
    setIsLoadingOrganizations(false);
  };

  const setValidations = () => {
    setIsLoadingValidations(true);
    let initialSchema = Yup.object().shape({});
    const connectionTagValidation = Yup.object({
      connectionTag: Yup.string()
        .trim()
        .strict()
        .required(),
    });
    initialSchema = initialSchema.concat(connectionTagValidation);
    const organizationIdValidation = Yup.object({
      organizationId: Yup.string()
        .ensure()
        .required(),
      organizationName: Yup.string()
        .trim()
        .strict(),
      organizationCode: Yup.string(),
    });
    initialSchema = initialSchema.concat(organizationIdValidation);
    const packageTypeValidation = Yup.object({
      hidden: Yup.number(),
      packages: Yup.string()
        .required('Please select at least two packages')
        .when('hidden', {
          is: x => x < 2,
          then: Yup.string()
            .required('Please select at least two packages')
            .matches(/(' {2}')/, { message: 'Please select at least two packages' }),
        }),
    });
    initialSchema = initialSchema.concat(packageTypeValidation);
    const acknowledgeCostsValidation = Yup.object({
      acknowledgeCosts: Yup.bool().oneOf([true], intl.formatMessage({id: "connection.acknowledgeAdditionalCosts.error"})),
    });
    initialSchema = initialSchema.concat(acknowledgeCostsValidation);
    const agreeToSValidation = Yup.object({
      agreeTos: Yup.bool().oneOf([true], intl.formatMessage({id: "connection.agreeToS.error"})),
    });
    initialSchema = initialSchema.concat(agreeToSValidation);
    setValidationSchema(initialSchema);
    setIsLoadingValidations(false);
  };

  const handleUpdateConnectionName = (value, position) => {
    setConnectionNameChunks(prevChunks => {
      if (prevChunks[position] === value) {
        return prevChunks;
      }

      const newChunks = [...prevChunks];
      newChunks[position] = value;

      const newConnectionName = newChunks.join('') === '' ? '' : newChunks.join(' - ');
      setConnectionName(newConnectionName);

      return newChunks;
    });
  };

  useEffect(() => {
    setValidations();
    loadOrganizations();
    loadAvailablePackages(selectedPackages);
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    loadAvailablePackages(selectedPackages);
  }, [selectedPackages]);

  const handleUpdateTag = value => {
    handleUpdateConnectionName(value, 2);
  };

  const handleSelectPackage = (packageType, setFieldValue) => {
    setFieldValue(`hidden`, selectedPackages.length + 1);
    dispatch(OnboardingActions.SetSelectedPackage(packageType));
    setSelectedPackages([...new Set([...selectedPackages, packageType])]);

    handleUpdateConnectionName([...selectedPackages, packageType].join(' '), 1);
  };

  const handleRemoveSelectedPackage = (packageType, setFieldValue) => {
    setFieldValue(`hidden`, selectedPackages.length - 1);
    const selectedPackagesAfterRemove = without(selectedPackages, packageType);
    setSelectedPackages(selectedPackagesAfterRemove);
    dispatch(OnboardingActions.RemovePackage(packageType));

    handleUpdateConnectionName(selectedPackagesAfterRemove.join(' '), 1);
  };

  const onSelectOrganization = value => {
    if (value) {
      handleUpdateConnectionName(value.organizationName, 0);
    } else {
      handleUpdateConnectionName('', 0);
    }
  };

  const onSubmitForm = async values => {
    values.packages = selectedPackages;
    values.connectionName = connectionName;
    if (values.organizationId.organizationId) {
      dispatch(OnboardingActions.SetSelectedOrganization(values.organizationId));
      values.organizationId = values.organizationId.organizationId;
    }
    onSubmit(values);
  };

  return (
    <OnboardingPackageSelectionForm
      formRef={formRef}
      loading={loading}
      initialValues={initialValues}
      organizations={organizations}
      onSelectOrganization={onSelectOrganization}
      availablePackages={availablePackages}
      selectedPackages={selectedPackages}
      onSelectPackage={handleSelectPackage}
      onRemoveSelectedPackage={handleRemoveSelectedPackage}
      onUpdateTag={handleUpdateTag}
      validationSchema={validationSchema}
      connectionName={connectionName}
      onSubmit={onSubmitForm}
    />
  );
};

OnboardingPackageSelectionStep.propTypes = {
  formRef: PropTypes.object.isRequired,
  onSubmit: PropTypes.func.isRequired,
};

export default OnboardingPackageSelectionStep;
